import {
	DataDrivenPropertyValueSpecification,
	ExpressionSpecification,
	GeoJSONSource,
	MapMouseEvent,
} from "mapbox-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocationResourceProximity, LocationType } from "velavu-js-api";
import {
	MapHelperInstance,
	useListenerSetupMapEffect,
	useStyleDependantMapEffect,
	useStyleSetupMapEffect,
} from "../../helper/map-helper";
import { geoJSONBlankCollection } from "./mapbox-injectable";
import DeviceMarker, {
	resolveDeviceMarkerCoordinates,
} from "../../data/device-marker";
import { Feature, Geometry } from "geojson";
import {
	buildDeviceEffectExpression,
	buildDeviceSymbolExpression,
	DeviceSymbolEffect,
} from "../../helper/map-device-symbols";
import { createVelavuMapboxPopup } from "../../mapbox-component/velavu-mapbox-popup";
import whitelabel from "../../whitelabel";

const mapboxIDDevice = "asset";
const mapboxIDDeviceMarker = "asset-marker";
const mapboxIDDeviceOuter = "asset-outer"; //hover or focus
const mapboxIDDeviceCluster = "asset-cluster";
const mapboxIDDeviceClusterText = "asset-cluster-text";
const mapboxIDDeviceRadius = "asset-radius";

const iconSizeDefault = 0.7;
const iconSizeLarge = 0.7;

const notCluster: ExpressionSpecification = ["!", ["has", "cluster_count"]];
const isCluster: ExpressionSpecification = [
	"all",
	["has", "cluster_count"],
	[">", ["get", "cluster_count"], 0],
];

const zoomFilter: ExpressionSpecification = [
	"any",
	["!", ["has", "displayZoomThreshold"]],
	[">=", ["zoom"], ["get", "displayZoomThreshold"]],
];

interface MapboxInjectableDevicesParams {
	mapInstance: MapHelperInstance;
	devicesSource?: DeviceMarker[];
	hoveredDeviceID?: string;
	selectedDeviceID?: string;
	onSelectDevice?: (id: string) => void;
	animateMovement?: boolean;
	belowLayer?: string;
	disableInteraction?: boolean;
}

interface InternalDeviceMarker extends DeviceMarker {
	coordinates: [number, number];
}

interface MarkerProperties {
	deviceID: string;
	inSite: boolean;
	online: boolean | undefined;
	hardware: string | undefined;
	name: string;
	coordinates: [number, number];
	lat: number;
	type: LocationType;
	accuracy: number | undefined;
	displayZoomThreshold: number | undefined;
	priority: number;
}

export default function useMapboxInjectableDevices(
	params: MapboxInjectableDevicesParams,
): string {
	const {
		animateMovement,
		devicesSource,
		disableInteraction,
		hoveredDeviceID,
		mapInstance,
		onSelectDevice,
		selectedDeviceID,
	} = params;

	const [devices, setDevices] = useState<
		InternalDeviceMarker[] | undefined
	>();

	const [hoverID, setHoverID] = useState<string | undefined>(undefined);
	const focusIDs = useRef<string[]>([]); // save hoveredDeviceID & selectedDeviceID in ref

	const hiddenDevices = useMemo((): Set<string> => {
		const hiddenDevices = new Set<string>();

		if (devicesSource === undefined) {
			return hiddenDevices;
		}

		for (const device of devicesSource) {
			const location = device.location;
			if (location.location_type !== LocationType.Proximity) {
				continue;
			}

			//If this device is selected, hide the corresponding device
			if (selectedDeviceID === device.deviceID) {
				hiddenDevices.add(location.device_id);
			}
			//Otherwise if the corresponding device exists, hide this device
			else if (
				devicesSource.some((it) => it.deviceID === location.device_id)
			) {
				hiddenDevices.add(device.deviceID);
			}
		}

		return hiddenDevices;
	}, [devicesSource, selectedDeviceID]);

	//Sync the devices state with the devicesSource parameter
	const updateDevices = useCallback(
		(time: number) => {
			const newDevices = devicesSource
				?.filter((device) => !hiddenDevices.has(device.deviceID))
				?.map((device): InternalDeviceMarker => {
					let coordinates: [number, number];
					if (animateMovement) {
						coordinates = resolveDeviceMarkerCoordinates(
							device,
							time,
						);
					} else {
						coordinates = device.location.coordinates;
					}

					return {
						...device,
						coordinates: coordinates,
					};
				});
			setDevices(newDevices);
		},
		[animateMovement, devicesSource, setDevices, hiddenDevices],
	);

	useEffect(() => {
		updateDevices(performance.now());
	}, [devicesSource, updateDevices]);

	//Resolve whether we have any devices that are in an active animation state
	const needsAnimationLoop = useMemo(() => {
		const currentTime = performance.now();

		return (
			devices?.some((device) => {
				const anim = device.anim;
				return anim !== undefined && currentTime < anim.endDate;
			}) ?? false
		);
	}, [devices]);

	//Call updateDevices() every frame while we have devices that are animating
	useEffect(() => {
		if (!animateMovement || !needsAnimationLoop) {
			return;
		}

		let isCanceled: boolean = false;
		const loop = (time: number) => {
			if (isCanceled) return;

			updateDevices(time);
			requestAnimationFrame(loop);
		};
		requestAnimationFrame(loop);

		return () => {
			isCanceled = true;
		};
	}, [animateMovement, needsAnimationLoop, updateDevices]);

	useStyleSetupMapEffect(mapInstance, (_, box) => {
		const metersToPixelsAtMaxZoom = (meters: ExpressionSpecification) => [
			"/",
			["/", meters, 78271.484 / 2 ** 22],
			["cos", ["*", ["get", "lat"], Math.PI / 180]],
		];

		box.addSource(mapboxIDDevice, {
			type: "geojson",
			data: geoJSONBlankCollection,
			generateId: true,
			// cluster: true,
			clusterMaxZoom: 14,
			clusterRadius: 100,
			clusterProperties: {
				cluster_count: ["+", ["case", ["get", "inSite"], 1, 0]],
			},
		});

		box.addLayer(
			{
				id: mapboxIDDeviceMarker,
				type: "symbol",
				source: mapboxIDDevice,
				layout: {
					"icon-image": buildDeviceSymbolExpression(
						["get", "hardware"],
						["get", "online"],
						["==", ["get", "type"], LocationType.Fixed],
					),
					"icon-allow-overlap": true,
					"icon-size": iconSizeDefault,
					"symbol-sort-key": [
						"match",
						["get", "type"],
						LocationType.GPS,
						1,
						0,
					],
				},
				filter: zoomFilter,
			},
			params.belowLayer,
		);
		box.addLayer(
			{
				id: mapboxIDDeviceOuter,
				type: "symbol",
				source: mapboxIDDevice,
				layout: {
					"icon-image": buildDeviceEffectExpression(
						["get", "hardware"],
						DeviceSymbolEffect.Hover,
					),
					"icon-allow-overlap": true,
					"icon-size": iconSizeLarge,
					"symbol-sort-key": [
						"match",
						["get", "type"],
						LocationType.GPS,
						1,
						0,
					],
				},
				filter: zoomFilter,
			},
			mapboxIDDeviceMarker,
		);
		box.addLayer(
			{
				id: mapboxIDDeviceRadius,
				type: "circle",
				source: mapboxIDDevice,
				paint: {
					"circle-radius": [
						"interpolate",
						["exponential", 2],
						["zoom"],
						0,
						0,
						22,
						metersToPixelsAtMaxZoom(["get", "accuracy"]),
					],
					"circle-color": whitelabel.palette.primary900,
					"circle-opacity": 0.15,
					"circle-stroke-width": 0.3,
					"circle-stroke-color": whitelabel.palette.primary900,
					"circle-pitch-alignment": "map",
				},
				filter: zoomFilter,
			},
			mapboxIDDeviceOuter,
		);
		box.addLayer({
			id: mapboxIDDeviceCluster,
			type: "symbol",
			source: mapboxIDDevice,
			filter: isCluster,
			layout: {
				"icon-image": "cluster",
				"icon-allow-overlap": true,
				"icon-size": 1,
			},
		});
		box.addLayer({
			id: mapboxIDDeviceClusterText,
			type: "symbol",
			source: mapboxIDDevice,
			filter: isCluster,
			layout: {
				"text-field": ["get", "cluster_count"],
				"text-size": 14,
				"text-allow-overlap": true,
			},
			paint: {
				"text-color": "white",
			},
		});
	});

	const tooltipPopup = useMemo(() => createVelavuMapboxPopup(), []);

	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			if (disableInteraction) return;

			box.on("mousemove", mapboxIDDeviceMarker, (event) => {
				// sort by icon overlap order
				const features = event.features?.slice().sort((a, b) => {
					const aProperties = a.properties as MarkerProperties;
					const bProperties = b.properties as MarkerProperties;

					//Check focus first
					const compareFocus: number =
						Number(
							focusIDs.current.some(
								(x) => x === bProperties.deviceID,
							),
						) -
						Number(
							focusIDs.current.some(
								(x) => x === aProperties.deviceID,
							),
						);
					if (compareFocus !== 0) {
						return compareFocus;
					}

					//Use marker priority
					return bProperties.priority - aProperties.priority;
				});
				if (features !== undefined && features.length !== 0) {
					const properties = features[0]
						.properties as MarkerProperties;

					if (
						properties.displayZoomThreshold !== undefined &&
						map.getZoom() < properties.displayZoomThreshold
					) {
						return;
					}
					setHoverID(properties.deviceID);
					// show popup
					const descriptionHTML = `<span>${properties.name}</span>`;
					const coordinates = JSON.parse(
						//Gets converted to string through Mapbox
						properties.coordinates as unknown as string,
					);
					tooltipPopup
						.setLngLat({
							lng: coordinates[0],
							lat: coordinates[1],
						})
						.setHTML(descriptionHTML);
					if (!tooltipPopup.isOpen()) tooltipPopup.addTo(map);
				}
			});
			box.on("mouseleave", mapboxIDDeviceMarker, () => {
				setHoverID(undefined);
				// hide popup
				tooltipPopup.remove();
			});

			//Clear the hover state, since we won't get a mouseleave
			//event once the listeners are removed
			return () => {
				setHoverID(undefined);
				tooltipPopup.remove();
			};
		},
		[tooltipPopup, disableInteraction],
	);

	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			if (disableInteraction) return;

			const onClick = (event: MapMouseEvent) => {
				// sort by icon overlap order
				const features = event.features
					? event.features.slice().sort((a, b) => {
							const compareFocus =
								Number(
									focusIDs.current.some(
										(x) => x === b.properties?.deviceID,
									),
								) -
								Number(
									focusIDs.current.some(
										(x) => x === a.properties?.deviceID,
									),
								);
							if (compareFocus !== 0) {
								return compareFocus;
							}
							return (
								Number(b.properties?.type == LocationType.GPS) -
								Number(a.properties?.type == LocationType.GPS)
							);
						})
					: [];
				const properties = features[0].properties as MarkerProperties;

				onSelectDevice?.(properties.deviceID);
			};

			box.on("click", mapboxIDDeviceMarker, onClick);
		},
		[mapInstance, onSelectDevice, disableInteraction],
	);

	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			focusIDs.current = [hoveredDeviceID, selectedDeviceID]
				.filter((value) => value !== undefined)
				.map((value) => String(value));
			const matchArray = [
				hoverID,
				hoveredDeviceID,
				selectedDeviceID,
			].filter(
				(value, index) =>
					value !== undefined &&
					[hoverID, hoveredDeviceID, selectedDeviceID].indexOf(
						value,
					) === index,
			);

			// if a BLEDevice is in focus, hide the GPSDevice it is paired to
			let hiddenID: string | undefined;
			if (matchArray.length > 0) {
				const focusedDevice = devices?.find(
					(device) =>
						device?.location?.location_type ===
							LocationType.Proximity &&
						matchArray.some((x) => x === device.deviceID),
				);
				if (
					focusedDevice?.location?.location_type ===
					LocationType.Proximity
				) {
					const location: LocationResourceProximity =
						focusedDevice.location;
					hiddenID = devices?.find(
						(device) =>
							device?.location?.location_type ===
								LocationType.GPS &&
							device.deviceID === location.device_id,
					)?.deviceID;
				}
			}

			const sortKey: DataDrivenPropertyValueSpecification<number> = [
				"match",
				["get", "deviceID"],
				hoverID === undefined ? "" : hoverID,
				5,
				matchArray.length > 0
					? [
							"match",
							["get", "deviceID"],
							matchArray,
							4,
							[
								"case",
								["==", ["get", "type"], LocationType.GPS],
								2,
								["get", "priority"],
							],
						]
					: [
							"case",
							["==", ["get", "type"], LocationType.GPS],
							2,
							["get", "priority"],
						],
			];

			map.setFilter(mapboxIDDeviceMarker, [
				"all",
				notCluster,
				["!=", ["get", "deviceID"], hiddenID ?? ""],
				zoomFilter,
			]);
			map.setFilter(mapboxIDDeviceOuter, [
				"all",
				["!", ["has", "cluster_count"]],
				["!=", ["get", "deviceID"], hiddenID ?? ""],
				matchArray.length > 0
					? [
							"match",
							["get", "deviceID"],
							matchArray,
							["literal", true],
							["literal", false],
						]
					: ["literal", false],
				zoomFilter,
			]);
			map.setLayoutProperty(
				mapboxIDDeviceMarker,
				"symbol-sort-key",
				sortKey,
			);
			map.setLayoutProperty(
				mapboxIDDeviceOuter,
				"symbol-sort-key",
				sortKey,
			);
			map.setLayoutProperty(
				mapboxIDDeviceMarker,
				"icon-size",
				matchArray.length > 0
					? [
							"match",
							["get", "deviceID"],
							matchArray,
							iconSizeLarge,
							iconSizeDefault,
						]
					: iconSizeDefault,
			);
			map.setLayoutProperty(
				mapboxIDDeviceOuter,
				"icon-image",
				buildDeviceEffectExpression(
					["get", "hardware"],
					[
						"match",
						["get", "deviceID"],
						selectedDeviceID ?? "",
						DeviceSymbolEffect.Focus,
						DeviceSymbolEffect.Hover,
					],
				),
			);
			map.setFilter(mapboxIDDeviceRadius, [
				"all",
				["!", ["has", "cluster_count"]],
				["has", "accuracy"],
				matchArray.length > 0
					? [
							"match",
							["get", "deviceID"],
							matchArray,
							["to-boolean", true],
							["to-boolean", false],
						]
					: ["to-boolean", false],
				zoomFilter,
			]);
		},
		[hoverID, hoveredDeviceID, selectedDeviceID, devices],
	);

	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			const assetSource = map.getSource(mapboxIDDevice) as GeoJSONSource;
			assetSource.setData({
				type: "FeatureCollection",
				features: (devices ?? []).map(
					(device): Feature<Geometry, MarkerProperties> => {
						return {
							type: "Feature",
							id: device.deviceID,
							geometry: {
								type: "Point",
								coordinates: device.coordinates,
							},
							properties: {
								deviceID: device.deviceID,
								inSite: device.inSite,
								online: device.online,
								hardware: device.hardware,
								name: device.name ?? device.deviceID,
								coordinates: device.coordinates,
								lat: device.coordinates[1],
								type: device.location?.location_type ?? "",
								accuracy: device.location.accuracy,
								displayZoomThreshold:
									device.displayZoomThreshold,
								priority: device.priority,
							},
						};
					},
				),
			});
		},
		[devices],
	);

	return mapboxIDDeviceRadius;
}
